/**
 * Entry point.
 *
 * Stylesheet order matters and is the same as 8loop's: fonts, then `index.css`
 * (Tailwind + the verbatim token files), then `app.css` — which is *unlayered* on
 * purpose, so its table-density rules win over the utilities they override.
 */

import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "./index.css";
import "./app.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@/design-system/layout/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { App } from "./App";
import { ApiError } from "./lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Nothing on a timer, anywhere. See lib/queries.ts for why.
      refetchOnWindowFocus: true,
      refetchInterval: false,
      staleTime: 15_000,
      // Retrying a 401 or a 422 just delays the message the user needs to read.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status < 500 ? false : failureCount < 2,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system" storageKey="callback:theme">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <Toaster richColors position="bottom-right" />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
